
import { useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { X, Upload, Link, Image, Video, ExternalLink } from 'lucide-react';
import { PROJECT_CATEGORIES } from '@/constants/projectCategories';

interface AddProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectAdded: () => void;
}

const AddProjectModal = ({ isOpen, onClose, onProjectAdded }: AddProjectModalProps) => {
  const [projectType, setProjectType] = useState<'link' | 'video'>('link');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [link, setLink] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailMethod, setThumbnailMethod] = useState<'url' | 'upload'>('url');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageMethod, setImageMethod] = useState<'url' | 'upload'>('url');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      // Clear URL when file is selected
      setImageUrl('');
    }
  };

  const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
      // Clear URL when file is selected
      setThumbnailUrl('');
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `project-images/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('project-images')
      .upload(filePath, file);

    if (uploadError) {
      throw uploadError;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('project-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !category) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields.",
      });
      return;
    }

    // Validate based on project type
    if (projectType === 'link') {
      if (!link) {
        toast({
          variant: "destructive",
          title: "Missing Link",
          description: "Please provide a project link.",
        });
        return;
      }

      // Validate image input based on selected method
      if (imageMethod === 'url' && !imageUrl) {
        toast({
          variant: "destructive",
          title: "Missing Image",
          description: "Please provide an image URL.",
        });
        return;
      }

      if (imageMethod === 'upload' && !imageFile) {
        toast({
          variant: "destructive",
          title: "Missing Image",
          description: "Please select an image file to upload.",
        });
        return;
      }
    } else if (projectType === 'video') {
      if (!videoUrl) {
        toast({
          variant: "destructive",
          title: "Missing Video URL",
          description: "Please provide a YouTube video URL.",
        });
        return;
      }

      // Validate thumbnail input based on selected method
      if (thumbnailMethod === 'url' && !thumbnailUrl) {
        toast({
          variant: "destructive",
          title: "Missing Thumbnail",
          description: "Please provide a thumbnail URL.",
        });
        return;
      }

      if (thumbnailMethod === 'upload' && !thumbnailFile) {
        toast({
          variant: "destructive",
          title: "Missing Thumbnail",
          description: "Please select a thumbnail file to upload.",
        });
        return;
      }
    }

    setIsLoading(true);

    try {
      console.log('Attempting to add project:', { title, description, category, projectType });
      
      let finalImageUrl = '';
      let finalThumbnailUrl = '';

      if (projectType === 'link') {
        // Handle image upload if upload method is selected
        if (imageMethod === 'upload' && imageFile) {
          console.log('Uploading image file...');
          finalImageUrl = await uploadImage(imageFile);
          console.log('Image uploaded successfully:', finalImageUrl);
        } else {
          finalImageUrl = imageUrl;
        }
      } else if (projectType === 'video') {
        // Handle thumbnail upload if upload method is selected
        if (thumbnailMethod === 'upload' && thumbnailFile) {
          console.log('Uploading thumbnail file...');
          finalThumbnailUrl = await uploadImage(thumbnailFile);
          console.log('Thumbnail uploaded successfully:', finalThumbnailUrl);
        } else {
          finalThumbnailUrl = thumbnailUrl;
        }
      }

      // Direct insert without authentication dependency
      const { data, error } = await supabase
        .from('projects')
        .insert({
          title,
          description: description || null,
          category,
          project_type: projectType,
          link: projectType === 'link' ? link : null,
          video_url: projectType === 'video' ? videoUrl : null,
          image: projectType === 'link' ? finalImageUrl : null,
          thumbnail_url: projectType === 'video' ? finalThumbnailUrl : null,
          created_by: null, // No authentication required for admin
        })
        .select();

      if (error) {
        console.error('Supabase error inserting project:', error);
        throw error;
      }

      console.log('Project inserted successfully:', data);

      toast({
        title: "Success",
        description: "Project added successfully!",
      });

      // Reset form
      setProjectType('link');
      setTitle('');
      setDescription('');
      setCategory('');
      setLink('');
      setVideoUrl('');
      setThumbnailUrl('');
      setThumbnailFile(null);
      setThumbnailMethod('url');
      setImageUrl('');
      setImageFile(null);
      setImageMethod('url');
      
      onProjectAdded();
      onClose();
    } catch (error: any) {
      console.error('Error adding project:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to add project. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={handleOverlayClick}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Add New Project</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Project Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Type *
            </label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => setProjectType('link')}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  projectType === 'link'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-300'
                }`}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Link
              </button>
              <button
                type="button"
                onClick={() => setProjectType('video')}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  projectType === 'video'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-300'
                }`}
              >
                <Video className="w-4 h-4 mr-2" />
                Video
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select Category</option>
              {PROJECT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Link Project Fields */}
          {projectType === 'link' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Link *
                </label>
                <input
                  type="url"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="https://example.com/project"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Image *
                </label>
                
                {/* Image Method Toggle */}
                <div className="flex items-center space-x-4 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setImageMethod('url');
                      setImageFile(null);
                    }}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      imageMethod === 'url'
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-300'
                    }`}
                  >
                    <Link className="w-4 h-4 mr-2" />
                    Use URL
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setImageMethod('upload');
                      setImageUrl('');
                    }}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      imageMethod === 'upload'
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-300'
                    }`}
                  >
                    <Image className="w-4 h-4 mr-2" />
                    Upload File
                  </button>
                </div>

                {/* URL Input */}
                {imageMethod === 'url' && (
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                )}

                {/* File Upload Input */}
                {imageMethod === 'upload' && (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      required
                    />
                    {imageFile && (
                      <p className="text-sm text-gray-600 mt-2">
                        Selected: {imageFile.name}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Video Project Fields */}
          {projectType === 'video' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  YouTube Video URL *
                </label>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video Thumbnail *
                </label>
                
                {/* Thumbnail Method Toggle */}
                <div className="flex items-center space-x-4 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setThumbnailMethod('url');
                      setThumbnailFile(null);
                    }}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      thumbnailMethod === 'url'
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-300'
                    }`}
                  >
                    <Link className="w-4 h-4 mr-2" />
                    Use URL
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setThumbnailMethod('upload');
                      setThumbnailUrl('');
                    }}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      thumbnailMethod === 'upload'
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-300'
                    }`}
                  >
                    <Image className="w-4 h-4 mr-2" />
                    Upload File
                  </button>
                </div>

                {/* URL Input */}
                {thumbnailMethod === 'url' && (
                  <input
                    type="url"
                    value={thumbnailUrl}
                    onChange={(e) => setThumbnailUrl(e.target.value)}
                    placeholder="https://example.com/thumbnail.jpg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                )}

                {/* File Upload Input */}
                {thumbnailMethod === 'upload' && (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailFileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      required
                    />
                    {thumbnailFile && (
                      <p className="text-sm text-gray-600 mt-2">
                        Selected: {thumbnailFile.name}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Add Project
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddProjectModal;
